import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {
  CalendarToday,
  Close,
  ExpandMore,
  FilterList,
  Search,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import SpinningSpark from '../shared/components/SpinningSpark';
import { emptyTemplateFilters } from './templateFilters.utils';

const STATUS_OPTIONS = [
  { label: 'Live', value: 'live' },
  { label: 'Upcoming', value: 'upcoming' },
  { label: 'Evergreen', value: 'evergreen' },
  { label: 'Ended', value: 'ended' },
];

const SORT_OPTIONS = [
  { label: 'Recently added', value: 'recently_added' },
  { label: 'Recently edited', value: 'recently_edited' },
  { label: 'Start date', value: 'start_date' },
  { label: 'End date', value: 'end_date' },
];

const TYPE_OPTIONS = [
  { label: 'Pre-written', value: 'pw' },
  { label: 'Product link', value: 'pl' },
  { label: 'Fill-in-the-blank', value: 'fitb' },
  { label: 'Editable videos', value: 'video' },
];

function selectedValues(value) {
  return String(value || '')
    .split(',')
    .filter(Boolean);
}

function toggleValue(value, option) {
  const selected = selectedValues(value);
  const next = selected.includes(option)
    ? selected.filter((item) => item !== option)
    : [...selected, option];

  return next.join(',');
}

function filtersForTypes(filters, types) {
  const selected = selectedValues(types);
  const categories = selected.filter((value) => value !== 'video');
  const postType =
    selected.length === 1 && categories.length === 1 ? categories[0] : '';

  return {
    ...filters,
    types,
    postType,
    topic: postType === 'pl' ? '' : filters.topic,
    subtopic: postType === 'pl' ? '' : filters.subtopic,
  };
}

function formatDateRange(dateFrom, dateTo) {
  const fromLabel = dateFrom ? moment(dateFrom).format('MM/DD/YY') : '';
  const toLabel = dateTo ? moment(dateTo).format('MM/DD/YY') : '';

  if (fromLabel && toLabel) return `${fromLabel} – ${toLabel}`;
  if (fromLabel) return `From ${fromLabel}`;
  if (toLabel) return `Until ${toLabel}`;
  return 'Date range';
}

function FilterCheckbox({ label, checked, onChange }) {
  return (
    <FormControlLabel
      sx={styles.checkboxLabel}
      control={
        <Checkbox
          size="small"
          checked={checked}
          onChange={onChange}
          sx={styles.checkbox}
        />
      }
      label={label}
    />
  );
}

FilterCheckbox.propTypes = {
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

function sortByActiveFirst(options, activeCounts, countKey) {
  return [...options]
    .map((option) => ({
      ...option,
      active: activeCounts[`${countKey}-${option.id}`] > 0,
    }))
    .sort((a, b) => {
      if (a.active === b.active) return 0;
      return a.active ? -1 : 1;
    });
}

function FilterAutocomplete({ label, options, valueId, disabled, onChange }) {
  const selected =
    options.find((option) => String(option.id) === String(valueId)) || null;
  const [inputValue, setInputValue] = useState(selected?.name || '');

  useEffect(() => {
    setInputValue(selected?.name || '');
  }, [selected?.name, valueId]);

  const clearSelection = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setInputValue('');
    onChange('');
  };

  return (
    <Autocomplete
      disabled={disabled}
      options={options}
      value={selected}
      inputValue={inputValue}
      onInputChange={(_event, nextValue, reason) => {
        if (reason === 'reset') {
          setInputValue(nextValue);
          return;
        }
        setInputValue(nextValue);
      }}
      onChange={(_event, option) => {
        onChange(option ? String(option.id) : '');
        setInputValue(option?.name || '');
      }}
      getOptionLabel={(option) => option.name || ''}
      isOptionEqualToValue={(option, value) =>
        String(option.id) === String(value.id)
      }
      renderOption={(props, option) => {
        // eslint-disable-next-line react/prop-types -- MUI injects key on option props
        const { key, ...optionProps } = props;
        return (
          // eslint-disable-next-line react/jsx-props-no-spreading -- MUI option props
          <li key={key} {...optionProps}>
            <Box sx={styles.optionRow}>
              <Box component="span" sx={styles.optionLabel}>
                {option.name}
              </Box>
              {option.active ? (
                <Box
                  component="span"
                  sx={styles.activeDot}
                  aria-label="Active"
                />
              ) : null}
            </Box>
          </li>
        );
      }}
      popupIcon={null}
      forcePopupIcon={false}
      disableClearable
      clearOnBlur
      noOptionsText="No matches"
      renderInput={(params) => (
        <TextField
          id={params.id}
          disabled={params.disabled}
          fullWidth={params.fullWidth}
          size={params.size}
          placeholder={label}
          inputProps={params.inputProps}
          InputLabelProps={params.InputLabelProps}
          // eslint-disable-next-line react/jsx-no-duplicate-props -- inputProps and InputProps are different MUI props
          InputProps={{
            ref: params.InputProps.ref,
            className: params.InputProps.className,
            startAdornment: params.InputProps.startAdornment,
            endAdornment: valueId ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={`Clear ${label}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={clearSelection}
                >
                  <Close sx={styles.fieldIcon} />
                </IconButton>
              </InputAdornment>
            ) : (
              <InputAdornment position="end" sx={styles.searchAdornment}>
                <Search sx={styles.fieldIcon} />
              </InputAdornment>
            ),
          }}
        />
      )}
      sx={styles.autocomplete}
    />
  );
}

FilterAutocomplete.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      active: PropTypes.bool,
    })
  ).isRequired,
  valueId: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

FilterAutocomplete.defaultProps = {
  valueId: '',
  disabled: false,
};

export default function TemplatesFilters({
  filterValues,
  appliedFilters,
  searchQuery,
  onFilterChange,
  activeCounts,
}) {
  const { topics } = filterValues;
  const [dateAnchor, setDateAnchor] = useState(null);
  const topicDisabled = appliedFilters.postType === 'pl';

  const subtopics = useMemo(() => {
    if (!appliedFilters.topic || !topics?.length) {
      return [];
    }

    const selectedTopic = topics.find(
      (topic) => String(topic.id) === String(appliedFilters.topic)
    );

    return selectedTopic?.subtopics || [];
  }, [appliedFilters.topic, topics]);

  const applyFilters = (nextFilters) => {
    onFilterChange(searchQuery, nextFilters);
  };

  const dateFrom = appliedFilters.dateFrom
    ? moment(appliedFilters.dateFrom)
    : null;
  const dateTo = appliedFilters.dateTo ? moment(appliedFilters.dateTo) : null;

  return (
    <Box sx={styles.panel}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <FilterList sx={styles.filterIcon} />
          <Typography sx={styles.panelTitle}>Filters</Typography>
        </Stack>
        <Button
          onClick={() => onFilterChange('', emptyTemplateFilters())}
          sx={styles.clearButton}
        >
          Clear
        </Button>
      </Stack>

      <Button
        fullWidth
        variant="outlined"
        onClick={(event) => setDateAnchor(event.currentTarget)}
        startIcon={<CalendarToday sx={styles.fieldIcon} />}
        endIcon={
          appliedFilters.dateFrom || appliedFilters.dateTo ? (
            <Close
              sx={styles.fieldIcon}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                applyFilters({
                  ...appliedFilters,
                  dateFrom: '',
                  dateTo: '',
                });
              }}
            />
          ) : (
            <ExpandMore sx={styles.fieldIcon} />
          )
        }
        sx={{
          ...styles.dateButton,
          color:
            appliedFilters.dateFrom || appliedFilters.dateTo
              ? '#1B2838'
              : '#3E5166',
        }}
      >
        <Box component="span" sx={styles.dateLabel}>
          {formatDateRange(appliedFilters.dateFrom, appliedFilters.dateTo)}
        </Box>
      </Button>
      <Popover
        open={Boolean(dateAnchor)}
        anchorEl={dateAnchor}
        onClose={() => setDateAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Stack spacing={2} sx={styles.datePopover}>
            <DatePicker
              label="From"
              value={dateFrom}
              onChange={(value) =>
                applyFilters({
                  ...appliedFilters,
                  dateFrom:
                    value && value.isValid() ? value.format('YYYY-MM-DD') : '',
                })
              }
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <DatePicker
              label="To"
              value={dateTo}
              onChange={(value) =>
                applyFilters({
                  ...appliedFilters,
                  dateTo:
                    value && value.isValid() ? value.format('YYYY-MM-DD') : '',
                })
              }
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Stack>
        </LocalizationProvider>
      </Popover>

      {topics === undefined ? (
        <Box display="flex" justifyContent="center" mt={1.5}>
          <SpinningSpark isSpinning />
        </Box>
      ) : (
        <FilterAutocomplete
          label="Topic"
          options={sortByActiveFirst(topics || [], activeCounts, 'topic')}
          valueId={appliedFilters.topic}
          disabled={topicDisabled}
          onChange={(topicId) =>
            applyFilters({
              ...appliedFilters,
              topic: topicId,
              subtopic: '',
            })
          }
        />
      )}

      <FilterAutocomplete
        label="Subtopic"
        options={sortByActiveFirst(subtopics, activeCounts, 'subtopic')}
        valueId={appliedFilters.subtopic}
        disabled={
          topicDisabled || !appliedFilters.topic || subtopics.length === 0
        }
        onChange={(subtopicId) =>
          applyFilters({
            ...appliedFilters,
            subtopic: subtopicId,
          })
        }
      />

      <Divider sx={styles.divider} />
      <Typography sx={styles.sectionTitle}>Status</Typography>
      {STATUS_OPTIONS.map((option) => (
        <FilterCheckbox
          key={option.value}
          label={option.label}
          checked={selectedValues(appliedFilters.statuses).includes(
            option.value
          )}
          onChange={() =>
            applyFilters({
              ...appliedFilters,
              statuses: toggleValue(appliedFilters.statuses, option.value),
              availability: '',
              type: '',
            })
          }
        />
      ))}

      <Divider sx={styles.divider} />
      <Typography sx={styles.sectionTitle}>Sort by</Typography>
      {SORT_OPTIONS.map((option) => (
        <FilterCheckbox
          key={option.value}
          label={option.label}
          checked={appliedFilters.sortBy === option.value}
          onChange={() =>
            applyFilters({
              ...appliedFilters,
              sortBy:
                appliedFilters.sortBy === option.value ? '' : option.value,
            })
          }
        />
      ))}

      <Divider sx={styles.divider} />
      <Typography sx={styles.sectionTitle}>Type</Typography>
      {TYPE_OPTIONS.map((option) => (
        <FilterCheckbox
          key={option.value}
          label={option.label}
          checked={selectedValues(appliedFilters.types).includes(option.value)}
          onChange={() =>
            applyFilters(
              filtersForTypes(
                appliedFilters,
                toggleValue(appliedFilters.types, option.value)
              )
            )
          }
        />
      ))}
    </Box>
  );
}

const styles = {
  panel: {
    width: { xs: '100%', md: 248 },
    flexShrink: 0,
    position: { xs: 'static', md: 'sticky' },
    top: { md: 24 },
    maxHeight: { md: 'calc(100vh - 48px)' },
    overflowY: { md: 'auto' },
    alignSelf: 'flex-start',
    backgroundColor: '#fff',
    border: '1px solid #E6EBF1',
    borderRadius: '16px',
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
  },
  panelTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#1B2838',
  },
  filterIcon: {
    fontSize: 20,
    color: '#1B2838',
  },
  clearButton: {
    minWidth: 0,
    padding: 0,
    textTransform: 'none',
    color: '#66788A',
    fontWeight: 500,
    fontSize: 14,
  },
  dateButton: {
    marginTop: '16px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    color: '#3E5166',
    borderColor: '#E3E8EF',
    borderRadius: '8px',
    height: 40,
    fontWeight: 400,
    paddingLeft: '12px',
    paddingRight: '8px',
    '& .MuiButton-endIcon': { marginLeft: 'auto' },
  },
  dateLabel: {
    flex: 1,
    textAlign: 'left',
    fontSize: 14,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  datePopover: {
    padding: 2,
    width: 280,
  },
  autocomplete: {
    marginTop: '12px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      height: 40,
      backgroundColor: '#fff',
      fontSize: 14,
      color: '#1B2838',
      paddingRight: '8px !important',
    },
    '& .MuiOutlinedInput-root.Mui-disabled': {
      backgroundColor: '#fff',
    },
    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E3E8EF',
    },
    '& .MuiInputBase-input.Mui-disabled': {
      WebkitTextFillColor: '#A8B6C4',
    },
    '& .Mui-disabled .MuiSvgIcon-root': {
      color: '#A8B6C4',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E3E8EF',
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#3E5166',
      opacity: 1,
    },
  },
  fieldIcon: {
    fontSize: 18,
    color: '#3E5166',
  },
  searchAdornment: {
    position: 'absolute',
    right: 10,
    pointerEvents: 'none',
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    width: '100%',
  },
  optionLabel: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  activeDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#1F8A4C',
    flexShrink: 0,
  },
  divider: {
    marginTop: '16px',
    marginBottom: '12px',
    borderColor: '#EEF2F6',
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#1B2838',
    marginBottom: '4px',
  },
  checkboxLabel: {
    marginLeft: 0,
    marginRight: 0,
    height: 28,
    '& .MuiFormControlLabel-label': {
      fontSize: 14,
      color: '#3E5166',
    },
  },
  checkbox: {
    padding: '4px',
    marginRight: '6px',
    color: '#C5D0DC',
    '&.Mui-checked': {
      color: '#0053e2',
    },
  },
};

TemplatesFilters.propTypes = {
  filterValues: PropTypes.shape({
    postTypes: PropTypes.arrayOf(PropTypes.shape({})),
    topics: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  appliedFilters: PropTypes.shape({
    postType: PropTypes.string,
    topic: PropTypes.string,
    subtopic: PropTypes.string,
    dateFrom: PropTypes.string,
    dateTo: PropTypes.string,
    statuses: PropTypes.string,
    sortBy: PropTypes.string,
    types: PropTypes.string,
  }).isRequired,
  searchQuery: PropTypes.string.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  activeCounts: PropTypes.objectOf(PropTypes.number).isRequired,
};
